/**
 * Soul Sheets Panel — turns CRM Soul-Form data + ticket history into
 * a one-page narrative Soul Sheet a concierge reads in 60 seconds.
 *
 * Mounts inside CustomerIntelligenceDashboard. Two halves:
 *   1. Trigger Calendar (top) — upcoming birthdays / anniversaries.
 *   2. Member picker → Soul Sheet drawer (bottom) — pick a member,
 *      generate / view their Soul Profile + Outreach drafts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import SoulCheatGuide from './SoulCheatGuide';
import {
  Sparkles, Calendar, RefreshCw, Search, Mail, MessageCircle,
  Copy, Check, X, Cake, Heart, ShieldCheck, ChevronRight, Users,
} from 'lucide-react';

const TIER_DOT = { RR: '#1C304A', RRS: '#5C5652', Unknown: '#B0B7BB' };

const KIND_LABELS = {
  welcome: 'Welcome',
  birthday: 'Birthday',
  anniversary: 'Anniversary',
  reactivation: 'Re-engage',
  seasonal: 'Seasonal',
};

const TRIGGER_ICON = {
  birthday: Cake,
  anniversary: Heart,
  verification_anniversary: ShieldCheck,
};

export default function SoulSheetsPanel({ clientId, isCeo }) {
  const [members, setMembers] = useState([]);
  const [memberMeta, setMemberMeta] = useState({ total: 0, soul: 0 });
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [city, setCity] = useState('');
  const [soulOnly, setSoulOnly] = useState(false);

  const [triggers, setTriggers] = useState([]);
  const [triggerDays, setTriggerDays] = useState(30);
  const [loadingTriggers, setLoadingTriggers] = useState(false);

  const [openMember, setOpenMember] = useState(null);  // member object

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (tier) params.tier = tier;
      if (city) params.city = city;
      if (soulOnly) params.soul_only = true;
      const r = await api.get(`/soul-engine/${clientId}/members`, { params });
      setMembers(r.data?.members || []);
      setMemberMeta({ total: r.data?.total_members || 0, soul: r.data?.soul_completed || 0 });
    } catch {
      setMembers([]);
    } finally { setLoadingMembers(false); }
  };

  const loadTriggers = async () => {
    setLoadingTriggers(true);
    try {
      const r = await api.get(`/soul-engine/${clientId}/triggers`, { params: { days: triggerDays } });
      setTriggers(r.data?.triggers || []);
    } catch {
      setTriggers([]);
    } finally { setLoadingTriggers(false); }
  };

  useEffect(() => { loadMembers(); /* eslint-disable-next-line */ }, [clientId, tier, soulOnly]);
  useEffect(() => { loadTriggers(); /* eslint-disable-next-line */ }, [clientId, triggerDays]);

  // Debounce free-text search
  useEffect(() => {
    const t = setTimeout(loadMembers, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const cities = useMemo(() => {
    const set = new Set(members.map(m => m.city).filter(Boolean));
    return Array.from(set).sort();
  }, [members]);

  return (
    <div className="space-y-4" data-testid="soul-sheets-panel">
      <SoulCheatGuide />
      {/* Trigger Calendar */}
      <Section title={<span className="inline-flex items-center gap-2"><Calendar size={13} /> Trigger Calendar · next {triggerDays} days</span>}>
        <div className="flex items-center gap-2 mb-3 text-[11px]">
          <span style={{ color: '#6B7280' }}>Window:</span>
          {[14, 30, 60, 90].map(d => (
            <button
              key={d}
              data-testid={`soul-trigger-window-${d}`}
              onClick={() => setTriggerDays(d)}
              className="px-2 py-0.5 rounded-full border"
              style={{
                borderColor: triggerDays === d ? '#1C304A' : '#E5E1DC',
                background: triggerDays === d ? '#1C304A' : 'white',
                color: triggerDays === d ? 'white' : '#1B2A4A',
              }}>{d}d</button>
          ))}
          <button
            onClick={loadTriggers}
            data-testid="soul-trigger-refresh"
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-gray-50"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <RefreshCw size={11} className={loadingTriggers ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        {triggers.length === 0 ? (
          <p className="text-[12px] py-1.5" style={{ color: '#9CA3AF' }}>
            {loadingTriggers ? 'Loading…' : `No birthdays / anniversaries in the next ${triggerDays} days.`}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="soul-triggers-grid">
            {triggers.slice(0, 24).map(t => {
              const Icon = TRIGGER_ICON[t.kind] || Calendar;
              const tone = t.kind === 'birthday' ? '#A86B2C'
                          : t.kind === 'anniversary' ? '#A32D58'
                          : '#2A7B88';
              return (
                <button
                  key={`${t.member_id}-${t.kind}`}
                  data-testid={`soul-trigger-${t.member_id}-${t.kind}`}
                  onClick={() => setOpenMember({
                    zoho_contact_id: t.member_id, name: t.name, city: t.city,
                    member_tier: t.tier, email: t.email, phone: t.phone,
                  })}
                  className="text-left p-2.5 rounded-lg border hover:bg-gray-50 transition"
                  style={{ borderColor: '#E5E1DC' }}>
                  <div className="flex items-start gap-2">
                    <div className="rounded p-1.5" style={{ background: tone + '15' }}>
                      <Icon size={13} style={{ color: tone }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold truncate" style={{ color: '#1B2A4A' }}>
                        {t.name}
                      </div>
                      <div className="text-[10.5px] flex items-center gap-1.5 mt-0.5" style={{ color: '#6B7280' }}>
                        <span style={{ color: tone, fontWeight: 600 }}>
                          {t.kind === 'verification_anniversary' ? 'Member-anniversary' :
                           t.kind.charAt(0).toUpperCase() + t.kind.slice(1)}
                        </span>
                        {t.tier && <span>· {t.tier}</span>}
                        {t.city && <span>· {t.city}</span>}
                      </div>
                      <div className="text-[10.5px] mt-0.5" style={{ color: '#1B2A4A' }}>
                        {new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {' · '}
                        <span style={{ color: t.days_until <= 7 ? '#A32D2D' : '#6B7280' }}>
                          {t.days_until === 0 ? 'today'
                            : t.days_until === 1 ? 'tomorrow'
                            : `in ${t.days_until} days`}
                        </span>
                        {t.milestone_years ? <span style={{ color: '#9CA3AF' }}> · {t.milestone_years}y</span> : null}
                      </div>
                    </div>
                    <ChevronRight size={13} style={{ color: '#9CA3AF', marginTop: 2 }} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {triggers.length > 24 && (
          <p className="text-[10.5px] mt-2 italic" style={{ color: '#9CA3AF' }}>
            Showing first 24 of {triggers.length} upcoming triggers.
          </p>
        )}
      </Section>

      {/* Member Picker */}
      <Section title={<span className="inline-flex items-center gap-2"><Users size={13} /> Soul Sheets · {memberMeta.soul.toLocaleString('en-IN')} of {memberMeta.total.toLocaleString('en-IN')} members have Soul Form completed</span>}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
            <Search size={11} style={{ color: '#9CA3AF' }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              data-testid="soul-search"
              className="text-[12px] outline-none bg-transparent w-48"
            />
          </div>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            data-testid="soul-tier-filter"
            className="text-[11px] px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All tiers</option>
            <option value="RR">RR</option>
            <option value="RRS">RRS</option>
          </select>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="soul-city-filter"
            className="text-[11px] px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All cities</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#1B2A4A' }}>
            <input
              type="checkbox"
              checked={soulOnly}
              onChange={(e) => setSoulOnly(e.target.checked)}
              data-testid="soul-only-filter"
            />
            Soul Form completed only
          </label>
          {city && (
            <button onClick={() => setCity('')} className="text-[11px] underline" style={{ color: '#6B7280' }}>clear city</button>
          )}
          <span className="ml-auto text-[10.5px]" style={{ color: '#9CA3AF' }}>
            {members.length} matching · click a row to open Soul Sheet
          </span>
        </div>
        {loadingMembers ? (
          <p className="text-[12px] py-3 text-center" style={{ color: '#9CA3AF' }}>Loading members…</p>
        ) : members.length === 0 ? (
          <p className="text-[12px] py-3 text-center" style={{ color: '#9CA3AF' }}>
            No members match. Try clearing filters, or run the CSV ingest to load Soul-Form data.
          </p>
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight: 360 }}>
            <table className="w-full text-[11px]">
              <thead className="sticky top-0" style={{ background: '#F7F5F2' }}>
                <tr className="text-left" style={{ color: '#6B7280' }}>
                  <th className="py-1.5 px-2">Member</th>
                  <th className="py-1.5 px-2">City</th>
                  <th className="py-1.5 px-2">Tier</th>
                  <th className="py-1.5 px-2">Soul</th>
                  <th className="py-1.5 px-2">Birthday</th>
                  <th className="py-1.5 px-2">Anniversary</th>
                  <th className="py-1.5 pl-2"></th>
                </tr>
              </thead>
              <tbody data-testid="soul-members-tbody">
                {members.map(m => (
                  <tr
                    key={m.zoho_contact_id}
                    onClick={() => setOpenMember(m)}
                    data-testid={`soul-member-row-${m.zoho_contact_id}`}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    style={{ borderColor: '#F0EEEA' }}>
                    <td className="py-1.5 px-2 font-medium truncate" style={{ color: '#1B2A4A', maxWidth: 200 }}>{m.name || '—'}</td>
                    <td className="py-1.5 px-2" style={{ color: '#6B7280' }}>{m.city || '—'}</td>
                    <td className="py-1.5 px-2 font-semibold" style={{ color: TIER_DOT[m.member_tier] || '#9CA3AF' }}>{m.member_tier || '—'}</td>
                    <td className="py-1.5 px-2">
                      {m.soul_form_completed ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#E5F4ED', color: '#1f5736' }}>✓ Yes</span>
                      ) : (
                        <span className="text-[10px]" style={{ color: '#9CA3AF' }}>—</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 tabular-nums" style={{ color: '#6B7280' }}>{(m.date_of_birth || '').slice(0, 10) || '—'}</td>
                    <td className="py-1.5 px-2 tabular-nums" style={{ color: '#6B7280' }}>{(m.date_of_anniversary || '').slice(0, 10) || '—'}</td>
                    <td className="py-1.5 pl-2 text-right">
                      {m.has_profile ? (
                        <span className="text-[10px] inline-flex items-center gap-0.5" style={{ color: '#2A7B5A' }}>
                          <Sparkles size={10} /> ready
                        </span>
                      ) : (
                        <ChevronRight size={12} style={{ color: '#9CA3AF' }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {openMember && (
        <SoulSheetDrawer
          clientId={clientId}
          member={openMember}
          isCeo={isCeo}
          onClose={() => setOpenMember(null)}
          onProfileGenerated={() => loadMembers()}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Drawer — full Soul Sheet for one member
// ────────────────────────────────────────────────────────────────────────
export function SoulSheetDrawer({ clientId, member, isCeo, onClose, onProfileGenerated }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [outreach, setOutreach] = useState({});  // kind → draft
  const [outreachBusy, setOutreachBusy] = useState({});  // kind → bool
  const [activeOutreach, setActiveOutreach] = useState('welcome');
  const [copied, setCopied] = useState('');
  // Push the Soul Sheet itself to Zoho — indestructible record.
  const [zohoSheetPushing, setZohoSheetPushing] = useState(false);
  const [zohoSheetTicket, setZohoSheetTicket] = useState(null);
  const [zohoSheetError, setZohoSheetError] = useState(null);

  const memberId = member.zoho_contact_id;
  const [soulForm, setSoulForm] = useState(null);

  useEffect(() => {
    setSoulForm(null);
    api.get(`/soul-engine/${clientId}/member/${memberId}/soul-form`)
      .then(r => setSoulForm(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [memberId]);

  const loadCached = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/soul-engine/${clientId}/profile/${memberId}`);
      setProfile(r.data);
      // If profile already has a Zoho ticket, surface it in the UI.
      if (r.data?.zoho_ticket_number) {
        setZohoSheetTicket({
          ticket_number: r.data.zoho_ticket_number,
          ticket_url: r.data.zoho_ticket_id
            ? `https://desk.zoho.in/agent/concierges/tickets/details/${r.data.zoho_ticket_id}`
            : null,
          pushed_at: r.data.zoho_pushed_at,
        });
      } else {
        setZohoSheetTicket(null);
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        setProfile(null);
      } else {
        setError(e?.response?.data?.detail || 'Failed to load profile');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCached(); /* eslint-disable-next-line */ }, [memberId]);

  const generateProfile = async (force = false) => {
    setGenerating(true); setError(null);
    try {
      const r = await api.post(
        `/soul-engine/${clientId}/profile/${memberId}${force ? '?force=true' : ''}`,
        null, { timeout: 120000 },
      );
      setProfile(r.data);
      onProfileGenerated && onProfileGenerated();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to generate profile');
    } finally { setGenerating(false); }
  };

  const pushSoulSheetToZoho = async () => {
    setZohoSheetPushing(true); setZohoSheetError(null);
    try {
      const r = await api.post(
        `/soul-engine/${clientId}/profile/${memberId}/push-to-zoho`,
        null, { timeout: 60000 },
      );
      setZohoSheetTicket(r.data);
    } catch (e) {
      setZohoSheetError(e?.response?.data?.detail || e.message || 'Push failed');
    } finally {
      setZohoSheetPushing(false);
    }
  };

  const generateOutreach = async (kind) => {
    setOutreachBusy(b => ({ ...b, [kind]: true }));
    try {
      const r = await api.post(`/soul-engine/${clientId}/outreach/${memberId}?kind=${kind}`, null, { timeout: 120000 });
      setOutreach(o => ({ ...o, [kind]: r.data }));
      setActiveOutreach(kind);
    } catch (e) {
      setOutreach(o => ({ ...o, [kind]: { error: e?.response?.data?.detail || e.message } }));
    } finally {
      setOutreachBusy(b => ({ ...b, [kind]: false }));
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch {}
  };

  const formatDate = (s) => {
    if (!s) return '—';
    try {
      const d = new Date(s.length === 10 ? s + 'T00:00:00' : s);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return s; }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: 'rgba(15, 26, 45, 0.4)' }}
      onClick={onClose}
      data-testid="soul-sheet-drawer">
      <div
        className="bg-white h-full w-full md:w-[640px] overflow-y-auto"
        style={{ boxShadow: '-8px 0 30px rgba(0,0,0,0.15)' }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 px-5 py-4 border-b flex items-start gap-3" style={{ background: '#1B2A4A', borderColor: '#3A4A6A' }}>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.18em] font-bold" style={{ color: '#E5C9A0' }}>
              SOUL SHEET · {clientId.toUpperCase()}
            </div>
            <h3 className="text-xl font-bold truncate" style={{ color: '#F7F5F2' }} data-testid="soul-sheet-name">
              {member.name || '—'}
            </h3>
            <div className="text-[11px] mt-0.5 flex items-center gap-2" style={{ color: '#B7C2D8' }}>
              {member.city && <span>{member.city}</span>}
              {member.member_tier && (
                <>
                  <span>·</span>
                  <span style={{ color: TIER_DOT[member.member_tier] === '#1C304A' ? '#E5C9A0' : '#B7C2D8' }}>
                    {member.member_tier}
                  </span>
                </>
              )}
              {profile?.lifetime_ticket_count !== undefined && (
                <>
                  <span>·</span>
                  <span>{profile.lifetime_ticket_count} requests lifetime</span>
                </>
              )}
              {profile?.days_since_last_ticket !== null && profile?.days_since_last_ticket !== undefined && (
                <>
                  <span>·</span>
                  <span>{profile.days_since_last_ticket}d silent</span>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="soul-sheet-close" className="p-1 rounded hover:bg-white/10" style={{ color: '#F7F5F2' }}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Profile block */}
          {loading ? (
            <p className="text-[12px]" style={{ color: '#9CA3AF' }}>Loading…</p>
          ) : error ? (
            <p className="text-[12px]" style={{ color: '#A32D2D' }}>{error}</p>
          ) : !profile ? (
            <div className="text-center py-8 rounded-lg border-2 border-dashed" style={{ borderColor: '#E5E1DC' }}>
              <Sparkles size={20} style={{ color: '#1C304A', margin: '0 auto 8px' }} />
              <p className="text-[13px] mb-3" style={{ color: '#1B2A4A' }}>
                No Soul Sheet generated yet for {member.name || 'this member'}.
              </p>
              <button
                onClick={() => generateProfile(false)}
                disabled={generating}
                data-testid="soul-generate-btn"
                className="px-4 py-2 rounded-md text-[12px] font-medium text-white inline-flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#1C304A' }}>
                <Sparkles size={13} />
                {generating ? 'Generating with Claude (~15s)…' : 'Generate Soul Sheet'}
              </button>
              <p className="text-[10.5px] mt-2 italic" style={{ color: '#9CA3AF' }}>
                Reads CRM Soul-Form data + service history. One Sonnet call · ~15-20 seconds.
              </p>
              <div className="text-left mt-4 space-y-4">
                <SoulFormAnswers soulForm={soulForm} defaultOpen />
                <MemberRequests clientId={clientId} memberId={memberId} />
              </div>
            </div>
          ) : (
            <>
              <SoulBlock
                label="Profile"
                onCopy={() => copy(profile.profile, 'profile')}
                copied={copied === 'profile'}
                content={profile.profile}
                renderer="prose"
              />
              {/* Concierge Notes — the intelligence layer (Soul-Form +
                  service history → recommendations). Falls back to a
                  helpful empty-state for legacy profiles generated
                  before the 5-block prompt. */}
              {profile.concierge_notes ? (
                <SoulBlock
                  label="Concierge notes (intelligence)"
                  onCopy={() => copy(profile.concierge_notes, 'notes')}
                  copied={copied === 'notes'}
                  content={profile.concierge_notes}
                  renderer="prose"
                />
              ) : profile.lifetime_ticket_count > 0 && (
                <div className="text-[11px] p-3 rounded border-2 border-dashed flex items-center gap-2" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                  <Sparkles size={13} style={{ color: '#C9A66B' }} />
                  <span className="flex-1">This Soul Sheet was generated before the Concierge Notes layer was added. Click Regenerate to add intelligence drawn from the {profile.lifetime_ticket_count} requests on file.</span>
                </div>
              )}
              <SoulBlock
                label="Experience suggestions"
                onCopy={() => copy(profile.experience_suggestions, 'suggestions')}
                copied={copied === 'suggestions'}
                content={profile.experience_suggestions}
                renderer="bullets"
              />
              <SoulBlock
                label="Welcome letter draft"
                onCopy={() => copy(profile.welcome_letter, 'welcome')}
                copied={copied === 'welcome'}
                content={profile.welcome_letter}
                renderer="prose"
              />
              <SoulBlock
                label="Quick replies (concierge codes)"
                onCopy={() => copy(profile.quick_replies, 'replies')}
                copied={copied === 'replies'}
                content={profile.quick_replies}
                renderer="prose"
              />

              {/* Push the SOUL SHEET to Zoho — indestructible record on the
                  member's Zoho contact profile. Survives any redeploy. */}
              <div className="rounded-lg p-3.5 border-2" style={{ borderColor: '#1C304A', background: '#F4F1EB' }}>
                {zohoSheetTicket ? (
                  <div className="flex items-start gap-2">
                    <Check size={16} style={{ color: '#1f5736', marginTop: 2 }} />
                    <div className="flex-1 text-[12px]" style={{ color: '#1B2A4A' }}>
                      <div className="font-semibold">Saved to Zoho · Ticket #{zohoSheetTicket.ticket_number}</div>
                      <div className="text-[10.5px] mt-0.5" style={{ color: '#6B7280' }}>
                        This Soul Sheet now lives permanently on {member.name || 'this member'}'s Zoho profile. Indestructible.
                      </div>
                      {zohoSheetTicket.ticket_url && (
                        <a
                          href={zohoSheetTicket.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="soul-sheet-zoho-link"
                          className="text-[11px] underline mt-1 inline-block"
                          style={{ color: '#1C304A' }}>
                          Open Soul Sheet ticket in Zoho Desk →
                        </a>
                      )}
                      <button
                        onClick={pushSoulSheetToZoho}
                        disabled={zohoSheetPushing}
                        data-testid="soul-sheet-push-zoho-again"
                        className="text-[10.5px] underline ml-3 disabled:opacity-50"
                        style={{ color: '#6B7280' }}>
                        {zohoSheetPushing ? 'Re-pushing…' : 'Push again (creates new ticket)'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5">
                    <div className="rounded p-2" style={{ background: '#1C304A' }}>
                      <Sparkles size={14} style={{ color: '#E5C9A0' }} />
                    </div>
                    <div className="flex-1 text-[11.5px]" style={{ color: '#1B2A4A' }}>
                      <strong>Save Soul Sheet to Zoho</strong>
                      <div className="text-[10.5px] mt-0.5" style={{ color: '#6B5439' }}>
                        Creates a permanent ticket on this member's Zoho profile with the full Soul Sheet — survives any ScaleBoard redeploy.
                      </div>
                    </div>
                    <button
                      onClick={pushSoulSheetToZoho}
                      disabled={zohoSheetPushing}
                      data-testid="soul-sheet-push-zoho"
                      className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
                      style={{ backgroundColor: '#1C304A' }}>
                      {zohoSheetPushing ? 'Saving…' : 'Save to Zoho'}
                    </button>
                  </div>
                )}
                {zohoSheetError && (
                  <div className="text-[11px] mt-2 p-2 rounded" style={{ background: '#FEE2E2', color: '#7a1f1f' }}>
                    {zohoSheetError}
                  </div>
                )}
              </div>

              {/* Service history */}
              <div>
                <SoulBlockHeader label={`Service history · last 15 of ${profile.lifetime_ticket_count || 0}`} />

                {/* Stats strip — cadence + favourite venues + pillar mix.
                    These are derived from the 60-ticket lookback in the
                    soul_engine pack. Hidden when there's no history. */}
                {profile.lifetime_ticket_count > 0 && (
                  <div className="text-[11px] mb-2 p-2 rounded space-y-1" style={{ background: '#F7F5F2' }}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold" style={{ color: '#1B2A4A' }}>Cadence:</span>
                      <span style={{ color: '#6B5439' }}>~{profile.avg_per_month || 0}/mo over {profile.active_months || 0} active month{profile.active_months === 1 ? '' : 's'}</span>
                      {profile.days_since_last_ticket !== null && profile.days_since_last_ticket !== undefined && (
                        <span style={{ color: '#6B7280' }}>· last {profile.days_since_last_ticket}d ago</span>
                      )}
                    </div>
                    {Object.keys(profile.top_pillars || {}).length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" style={{ color: '#1B2A4A' }}>Most used:</span>
                        {Object.entries(profile.top_pillars).slice(0, 5).map(([k, v]) => (
                          k && k !== '—' ? (
                            <span key={k} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'white', color: '#2A7B88', border: '1px solid #E5E1DC' }}>
                              {k} ({v})
                            </span>
                          ) : null
                        ))}
                      </div>
                    )}
                    {Object.keys(profile.top_venues || {}).length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" style={{ color: '#1B2A4A' }}>Favourite venues:</span>
                        {Object.entries(profile.top_venues).slice(0, 5).map(([k, v]) => (
                          <span key={k} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#FFF4E0', color: '#A86B2C' }}>
                            {k} ({v} visit{v === 1 ? '' : 's'})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {(profile.service_history || []).length === 0 ? (
                  <p className="text-[12px]" style={{ color: '#9CA3AF' }}>No tickets on record yet.</p>
                ) : (
                  <div className="text-[11px] space-y-1.5">
                    {(profile.service_history || []).slice(0, 15).map(t => (
                      <div key={t.ticket_number} className="flex items-start gap-2 p-2 rounded" style={{ background: '#F7F5F2' }}>
                        <span className="tabular-nums" style={{ color: '#9CA3AF', minWidth: 70 }}>
                          {(t.created_date || '').slice(0, 10) || '—'}
                        </span>
                        <span style={{ color: '#2A7B88', minWidth: 100 }}>{t.pillar || t.benefit || '—'}</span>
                        <span className="flex-1 truncate" style={{ color: '#1B2A4A' }} title={t.subject}>{t.subject || '—'}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9.5px] font-medium" style={{
                          background: (t.status || '').toLowerCase().includes('closed') ? '#E5F4ED' : '#FEF3C7',
                          color:      (t.status || '').toLowerCase().includes('closed') ? '#1f5736' : '#92400E',
                        }}>{t.status || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Outreach drafts */}
              <div>
                <SoulBlockHeader label="Personalised outreach" />
                <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                  {Object.entries(KIND_LABELS).map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => generateOutreach(k)}
                      disabled={outreachBusy[k]}
                      data-testid={`soul-outreach-${k}`}
                      className="px-2.5 py-1 rounded-full text-[11px] font-medium border transition disabled:opacity-50"
                      style={{
                        borderColor: outreach[k] ? '#1C304A' : '#E5E1DC',
                        background: outreach[k] ? '#1C304A' : 'white',
                        color: outreach[k] ? 'white' : '#1B2A4A',
                      }}>
                      {outreachBusy[k] ? `${label}…` : (outreach[k] ? `✓ ${label}` : label)}
                    </button>
                  ))}
                </div>
                {Object.keys(outreach).length > 0 && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.keys(outreach).map(k => (
                        <button
                          key={k}
                          onClick={() => setActiveOutreach(k)}
                          data-testid={`soul-outreach-tab-${k}`}
                          className="text-[10.5px] px-2 py-0.5 rounded"
                          style={{
                            background: activeOutreach === k ? '#F7F5F2' : 'white',
                            color: activeOutreach === k ? '#1B2A4A' : '#6B7280',
                            fontWeight: activeOutreach === k ? 600 : 400,
                          }}>
                          {KIND_LABELS[k]}
                        </button>
                      ))}
                    </div>
                    {outreach[activeOutreach] && (
                      <OutreachCard
                        draft={outreach[activeOutreach]}
                        onCopyEmail={() => copy(
                          (outreach[activeOutreach].subject ? `Subject: ${outreach[activeOutreach].subject}\n\n` : '') +
                          (outreach[activeOutreach].email || ''),
                          `email-${activeOutreach}`,
                        )}
                        onCopyWhats={() => copy(outreach[activeOutreach].whatsapp || '', `whats-${activeOutreach}`)}
                        copied={copied}
                        kind={activeOutreach}
                        clientId={clientId}
                        memberId={memberId}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Soul Form answers — always live off the member record */}
              <SoulFormAnswers soulForm={soulForm} />

              {/* Full request history — tap to read the conversation */}
              <MemberRequests clientId={clientId} memberId={memberId} />

              {/* Footer actions */}
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}>
                <button
                  onClick={() => generateProfile(true)}
                  disabled={generating || !isCeo}
                  data-testid="soul-regenerate-btn"
                  className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
                  <RefreshCw size={11} className={generating ? 'animate-spin' : ''} />
                  {generating ? 'Regenerating…' : 'Regenerate'}
                </button>
                <span className="text-[10px] ml-auto" style={{ color: '#9CA3AF' }}>
                  Born: {formatDate(profile.date_of_birth)} · Anniversary: {formatDate(profile.date_of_anniversary)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


const STATUS_PILL = (s) => {
  const str = s || '';
  if (str.startsWith('Closed')) return { bg: '#E7F2EA', fg: '#1E6B45' };
  if (str.startsWith('On Hold') || str === 'Onhold') return { bg: '#FDF3E1', fg: '#8A5B00' };
  return { bg: '#E8EEF9', fg: '#1B4A8A' };
};

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export function MemberRequests({ clientId, memberId }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [threads, setThreads] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    setItems(null); setOpenId(null); setThreads({});
    api.get(`/soul-engine/${clientId}/member/${memberId}/requests`)
      .then(r => setItems(r.data.items)).catch(() => setItems([]));
    // eslint-disable-next-line
  }, [memberId]);

  const toggle = async (t) => {
    const id = t.zoho_ticket_id;
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!threads[id]) {
      setBusy(b => ({ ...b, [id]: true }));
      try {
        const r = await api.get(`/soul-engine/${clientId}/ticket/${id}/thread`, { timeout: 90000 });
        setThreads(th => ({ ...th, [id]: r.data }));
      } catch {
        setThreads(th => ({ ...th, [id]: { error: true } }));
      } finally {
        setBusy(b => ({ ...b, [id]: false }));
      }
    }
  };

  if (!items || !items.length) return null;
  return (
    <div data-testid="member-requests-section">
      <div className="text-[10px] tracking-[0.18em] font-bold uppercase mb-1" style={{ color: '#2A7B88' }}>
        Their requests ({items.length})
      </div>
      <p className="text-[10.5px] mb-2" style={{ color: '#9CA3AF' }}>
        Tap any request to read the full conversation with the member.
      </p>
      <div className="space-y-1.5">
        {items.map((t) => {
          const pill = STATUS_PILL(t.status);
          const open = openId === t.zoho_ticket_id;
          const th = threads[t.zoho_ticket_id];
          return (
            <div key={t.zoho_ticket_id} className="rounded-lg border overflow-hidden" style={{ borderColor: open ? '#C9B896' : '#E5E1DC' }}>
              <button onClick={() => toggle(t)} data-testid={`member-request-${t.zoho_ticket_id}`}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-[#FBF9F5]">
                <MessageCircle size={14} style={{ color: '#B88C40', flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium truncate" style={{ color: '#1B2A4A' }}>
                    {t.subject || `Request #${t.ticket_number}`}
                  </div>
                  <div className="text-[10px]" style={{ color: '#9CA3AF' }}>
                    {fmtWhen(t.created_date)}
                    {t.pillar ? ` · ${t.pillar}` : ''}{t.establishment ? ` · ${t.establishment}` : ''}
                  </div>
                </div>
                <span className="text-[9.5px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap" style={{ background: pill.bg, color: pill.fg }}>
                  {(t.status || '—').split(':')[0]}
                </span>
                <ChevronRight size={13} style={{ color: '#9CA3AF', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }} />
              </button>
              {open && (
                <div className="px-3 pb-3 pt-1 border-t" style={{ borderColor: '#F0EDE7', background: '#FCFBF8' }} data-testid={`member-request-thread-${t.zoho_ticket_id}`}>
                  {busy[t.zoho_ticket_id] ? (
                    <p className="text-[11px] py-2" style={{ color: '#9CA3AF' }}>Fetching the full conversation from Zoho…</p>
                  ) : th?.error ? (
                    <p className="text-[11px] py-2" style={{ color: '#A32D2D' }}>Couldn't fetch this conversation from Zoho right now — try again in a minute.</p>
                  ) : th && th.messages?.length ? (
                    <div className="space-y-2 mt-2 max-h-80 overflow-y-auto pr-1">
                      {th.messages.map((m, i) => {
                        const isMember = m.direction === 'in';
                        return (
                          <div key={i} className={`flex ${isMember ? 'justify-start' : 'justify-end'}`}>
                            <div className="max-w-[85%] rounded-lg px-2.5 py-1.5"
                              style={{ background: isMember ? '#F3EFE7' : '#1B2A4A' }}>
                              <div className="text-[9px] font-semibold mb-0.5" style={{ color: isMember ? '#8A7B5C' : '#C9B896' }}>
                                {isMember ? 'Member' : 'Concierge'}{m.author ? ` · ${m.author}` : ''} · {fmtWhen(m.created_time)}
                              </div>
                              <div className="text-[11px] whitespace-pre-wrap" style={{ color: isMember ? '#3A3A3A' : '#F7F5F2' }}>
                                {m.content || '(no text)'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : th ? (
                    <p className="text-[11px] py-2" style={{ color: '#9CA3AF' }}>No conversation messages found on this ticket.</p>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


const SOUL_SOURCE_LABELS = {
  dreamweaver: 'DreamWeaver form',
  scaleboard_form: 'ScaleBoard form',
  zoho_ticket: 'Zoho SOUL ticket',
};

export function SoulFormAnswers({ soulForm, defaultOpen = false }) {
  if (!soulForm || !soulForm.count) return null;
  const sourceLabel = SOUL_SOURCE_LABELS[soulForm.source] || (soulForm.source ? soulForm.source.replace(/_/g, ' ') : 'Zoho CRM');
  return (
    <details open={defaultOpen} className="rounded p-2.5" style={{ background: '#FFF8EE' }} data-testid="soul-form-answers">
      <summary className="text-[11px] font-medium cursor-pointer flex flex-wrap items-center gap-1.5" style={{ color: '#8A4B00' }} data-testid="soul-form-audit">
        <span>Soul-Form answers ({soulForm.count} on file)</span>
        <span className="px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold" style={{ background: '#E9D9BC', color: '#6B4A00' }} data-testid="soul-form-source-badge">
          {sourceLabel}
        </span>
        {soulForm.enriched_at && (
          <span className="px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold" style={{ background: '#D9EDE2', color: '#1E6B45' }} data-testid="soul-form-enriched-badge">
            + enrichment
          </span>
        )}
      </summary>
      <div className="text-[10.5px] mt-2 space-y-1" style={{ color: '#6B5439' }}>
        {Object.entries(soulForm.answers).map(([k, v]) => (
          <div key={k}>
            <span className="font-semibold" style={{ color: '#8A4B00' }}>{k.replace(/_/g, ' ')}:</span>{' '}
            <span>{v}</span>
          </div>
        ))}
      </div>
    </details>
  );
}


function SoulBlockHeader({ label }) {
  return (
    <div className="text-[10px] tracking-[0.18em] font-bold uppercase mb-1.5" style={{ color: '#2A7B88' }}>
      {label}
    </div>
  );
}

function SoulBlock({ label, content, onCopy, copied, renderer = 'prose' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <SoulBlockHeader label={label} />
        <button
          onClick={onCopy}
          data-testid={`soul-copy-${label.toLowerCase().replace(/\s/g, '-')}`}
          className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100"
          style={{ color: copied ? '#2A7B5A' : '#9CA3AF' }}>
          {copied ? <Check size={10} /> : <Copy size={10} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="text-[12.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>
        {renderer === 'bullets'
          ? <BulletText text={content} />
          : <ProseText text={content} />}
      </div>
    </div>
  );
}

function ProseText({ text }) {
  if (!text) return <span style={{ color: '#9CA3AF' }}>—</span>;
  // Render *italic* between asterisks; preserve line breaks.
  const parts = text.split(/(\*[^*]+\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('*') && p.endsWith('*') && p.length > 2
          ? <em key={i} style={{ color: '#1C304A' }}>{p.slice(1, -1)}</em>
          : <span key={i}>{p}</span>,
      )}
    </>
  );
}

function BulletText({ text }) {
  if (!text) return <span style={{ color: '#9CA3AF' }}>—</span>;
  const lines = text.split('\n').map(l => l.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
  return (
    <ul className="list-disc pl-5 space-y-1">
      {lines.map((l, i) => <li key={i}><ProseText text={l} /></li>)}
    </ul>
  );
}

function OutreachCard({ draft, onCopyEmail, onCopyWhats, copied, kind, clientId, memberId }) {
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(null);  // {ticket_number, ticket_url}
  const [pushError, setPushError] = useState(null);

  const pushToZoho = async () => {
    setPushing(true); setPushError(null);
    try {
      const r = await api.post(`/soul-engine/${clientId}/outreach/${memberId}/push-to-zoho`, {
        kind,
        subject: draft.subject || null,
        email_body: draft.email || '',
        whatsapp_body: draft.whatsapp || null,
      }, { timeout: 60000 });
      setPushed(r.data);
    } catch (e) {
      setPushError(e?.response?.data?.detail || e.message || 'Push to Zoho failed');
    } finally {
      setPushing(false);
    }
  };

  if (draft.error) {
    return <div className="text-[12px] p-2.5 rounded" style={{ background: '#FEE2E2', color: '#7a1f1f' }}>
      {draft.error}
    </div>;
  }
  return (
    <div className="space-y-2">
      <div className="rounded p-3 border" style={{ borderColor: '#E5E1DC', background: '#FFFDF8' }}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] tracking-[0.18em] font-bold inline-flex items-center gap-1" style={{ color: '#A86B2C' }}>
            <Mail size={10} /> EMAIL · {KIND_LABELS[kind]}
          </div>
          <button
            onClick={onCopyEmail}
            data-testid={`soul-outreach-copy-email-${kind}`}
            className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-white"
            style={{ color: copied === `email-${kind}` ? '#2A7B5A' : '#9CA3AF' }}>
            {copied === `email-${kind}` ? <Check size={10} /> : <Copy size={10} />} Copy
          </button>
        </div>
        {draft.subject && (
          <div className="text-[11.5px] mb-1.5" style={{ color: '#1B2A4A' }}>
            <span style={{ color: '#9CA3AF' }}>Subject: </span>
            <span className="font-semibold">{draft.subject}</span>
          </div>
        )}
        <div className="text-[12px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>
          {draft.email || <span style={{ color: '#9CA3AF' }}>—</span>}
        </div>
        {draft.email_to && (
          <div className="text-[10px] mt-2" style={{ color: '#9CA3AF' }}>To: {draft.email_to}</div>
        )}
      </div>
      <div className="rounded p-3 border" style={{ borderColor: '#E5E1DC', background: '#F0FAF4' }}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] tracking-[0.18em] font-bold inline-flex items-center gap-1" style={{ color: '#1f5736' }}>
            <MessageCircle size={10} /> WHATSAPP · {KIND_LABELS[kind]}
          </div>
          <button
            onClick={onCopyWhats}
            data-testid={`soul-outreach-copy-whatsapp-${kind}`}
            className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-white"
            style={{ color: copied === `whats-${kind}` ? '#2A7B5A' : '#9CA3AF' }}>
            {copied === `whats-${kind}` ? <Check size={10} /> : <Copy size={10} />} Copy
          </button>
        </div>
        <div className="text-[12px] leading-relaxed whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>
          {draft.whatsapp || <span style={{ color: '#9CA3AF' }}>—</span>}
        </div>
        {draft.phone_to && (
          <div className="text-[10px] mt-2" style={{ color: '#9CA3AF' }}>To: {draft.phone_to}</div>
        )}
      </div>

      {/* Push to Zoho — closes the loop. Posts the drafts as a Zoho Desk
          ticket on the member's profile so the LSM reviews + sends from
          their normal Zoho queue. */}
      <div className="rounded p-3 border" style={{ borderColor: '#E5C9A0', background: '#FFF8EE' }}>
        {pushed ? (
          <div className="flex items-start gap-2">
            <Check size={14} style={{ color: '#1f5736', marginTop: 2 }} />
            <div className="flex-1 text-[12px]" style={{ color: '#1B2A4A' }}>
              <div className="font-semibold">Pushed to Zoho Desk · #{pushed.ticket_number}</div>
              <div className="text-[10.5px] mt-0.5" style={{ color: '#6B7280' }}>
                Subject: {pushed.subject}
              </div>
              {pushed.ticket_url && (
                <a
                  href={pushed.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`soul-zoho-link-${kind}`}
                  className="text-[11px] underline mt-1 inline-block"
                  style={{ color: '#1C304A' }}>
                  Open ticket in Zoho Desk →
                </a>
              )}
              <div className="text-[10.5px] mt-1.5 italic" style={{ color: '#6B5439' }}>
                Team will review and click Reply in Zoho to send to the member.
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1 text-[11px]" style={{ color: '#6B5439' }}>
              <strong>Ready to send?</strong> Push these drafts as a Zoho Desk ticket on {draft.name || 'this member'}'s profile —
              the team reviews + clicks Reply in Zoho to send to the member.
            </div>
            <button
              onClick={pushToZoho}
              disabled={pushing}
              data-testid={`soul-push-zoho-${kind}`}
              className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
              style={{ backgroundColor: '#1C304A' }}>
              {pushing ? 'Pushing…' : 'Push to Zoho'}
            </button>
          </div>
        )}
        {pushError && (
          <div className="text-[11px] mt-2 p-2 rounded" style={{ background: '#FEE2E2', color: '#7a1f1f' }}>
            {pushError}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
      <div className="text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: '#2A7B88' }}>{title}</div>
      {children}
    </div>
  );
}
